import {_} from 'lodash';
import * as ko from "knockout";
import {global} from "../../../appContext";
import {storeObservable} from "../../../util/sessionstore";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {IRouteDefinition, IRouteParams, TRouteId} from "../../../index";
import {getDisplayNameForRouteData, getObjectFromRouteData} from "../../../util/request";
import {_d, decodeAndStripHtml} from "../../../util/misc";

export interface ILatestObjectRaw {
    bookmark: boolean
    id: number
    index: number
    name: string
    orgnr: string
    params: IRouteParams
    routeId: TRouteId
    sequencenr?: number
}

export interface ILatestObject extends Omit<ILatestObjectRaw, 'bookmark'> {
    bookmark: KnockoutObservable<boolean>
}

export class LatestObjectsManager {
    private _storeList: KnockoutObservable<ILatestObjectRaw[]> = storeObservable('latest.objects.store.list');
    private list: KnockoutObservableArray<ILatestObject>;
    private storeLimit: KnockoutObservable<number> = storeObservable('latest.objects.store.limit');
    private showLimit: KnockoutObservable<number> = storeObservable('latest.objects.show.limit');
    private kosub: KOSubscriptionTracker = new KOSubscriptionTracker();
    private silent: boolean = false;

    constructor() {
        this.list = ko.observableArray(this._storeList().map(this.toDynamic));
        this.kosub.subscribe(this._storeList, () => {
            if (!this.silent) {
                this.list(this._storeList().map(this.toDynamic));
            }
        });
    }

    public add(data: any[], def: IRouteDefinition, params: IRouteParams) {
        if (!data || !def.isSubitem) {
            return;
        }
        const arr = this.list();
        const obj = getObjectFromRouteData(data, def, params);
        if (!obj) {
            _d.warn(`Could not find data object for ${def.id}.`);
            return;
        }
        const existing = this.list().find(o => o.id == obj.id && o.routeId == def.id);
        if (!existing) {
            arr.unshift(this.convertObjToLatest(obj, def, params));
        } else {
            this.refreshLatestObject(existing, obj, def, params);
        }
        arr.length = Math.min(arr.length, this.storeLimit());
        const latestObjects = _.sortBy(arr, 'index').reverse();
        this.list(latestObjects);
        this.updateStore();
        this.removeSurplus(def.id);
    }

    private getOrgnr(obj: any): string {
        return obj.homeorg ?? obj.orgnr ?? obj.orgid ?? global.login.selectedOrganizationId() ?? '';
    }

    public remove(obj: ILatestObject) {
        const o = this.list().find(o => o.params['id'] == obj.params['id'] && o.orgnr == obj.orgnr);
        if (o) {
            this.list.remove(o);
        }
        this.updateStore();
    }

    public removeById(id: number, orgnr: string = global.login.selectedOrganizationId() ?? '') {
        const o = this.list().find(o => o.params['id'] == id && o.orgnr == orgnr);
        if (o) {
            this.list.remove(o);
        }
        this.updateStore();
    }

    private refreshLatestObject(existing: ILatestObject, obj: any, def: IRouteDefinition, params: IRouteParams) {
        existing.name = decodeAndStripHtml(getDisplayNameForRouteData(obj, def, params));
        existing.params = params;
        if (def.id === 'importhistorydetails') {
            existing.sequencenr = obj.sequencenr;
        }
    }

    private convertObjToLatest(obj: any, def: IRouteDefinition, params: IRouteParams): ILatestObject {
        const ret: ILatestObject = {
            bookmark: ko.observable(false),
            index: this.getNextIndex(),
            name: decodeAndStripHtml(getDisplayNameForRouteData(obj, def, params)),
            orgnr: this.getOrgnr(obj),
            params: params,
            routeId: def.id,
            id: obj.id,
        }
        switch (def.id) {
            case 'importhistorydetails': {
                ret.id = params['importfileid'];
                ret.sequencenr = obj.sequencenr;
            }
        }
        return ret;
    }

    public getList(type: TRouteId): ILatestObject[] {
        const regular = this.getListByRegular(type).slice(0, this.showLimit());
        const bookmark = this.getListByBookmark(type);
        return _([...bookmark, ...regular])
            .map((o: ILatestObject) => _.clone(o)) // to trigger a DOM update
            .sortBy((o: ILatestObject) => o.index)
            .reverse()
            .value();
    }

    private _getList(routeId: TRouteId): ILatestObject[] {
        const orgnr = global.login.selectedOrganizationId() ?? '';
        return _(this.list())
            .filter((o: ILatestObject) => o.orgnr == orgnr && o.routeId == routeId)
            .sortBy((o: ILatestObject) => o.index)
            .reverse()
            .value()
            ;
    }

    public getListByRegular(type: TRouteId): ILatestObject[] {
        return this._getList(type).filter(o => !o.bookmark());
    }

    public getListByBookmark(type: TRouteId): ILatestObject[] {
        return this._getList(type).filter(o => o.bookmark());
    }

    public toggleBookmark(obj: ILatestObject, bool?: boolean) {
        const o = this.list().find(o => o.id == obj.id && o.routeId == obj.routeId);
        if (!o) {
            return;
        }
        o.bookmark(bool ?? !o.bookmark());
        this.removeSurplus(obj.routeId);
        this.list.valueHasMutated?.();
        this.updateStore();
    }

    public toggleBookmarkById(id: number, orgnr: string = global.login.selectedOrganizationId() ?? '') {
        const o = this.list().find(o => o.params['id'] == id && o.orgnr == orgnr);
        o?.bookmark(!o.bookmark());
        this.updateStore();
    }

    public isBookmarkedById(id: number, orgnr: string = global.login.selectedOrganizationId() ?? '') {
        const o = this.list().find(o => o.params['id'] == id && o.orgnr == orgnr);
        return o?.bookmark() ?? null;
    }

    public isListed(id: number, orgnr: string = global.login.selectedOrganizationId() ?? ''): boolean {
        const o = this.list().find(o => o.params['id'] == id && o.orgnr == orgnr);
        return !!o;
    }

    private getNextIndex(): number {
        return Math.max(...this.list().map(o => o.index), 0) + 1;
    }

    private removeSurplus(type: TRouteId) {
        let arr = this.list();
        const regulars = this.getListByRegular(type);
        if (regulars.length > this.showLimit()) {
            const regulars2remove = _.sortBy(regulars, (o: ILatestObject) => o.index).reverse().slice(this.showLimit());
            arr = arr.filter(o => !regulars2remove.includes(o));
            this.list(arr);
        }
    }

    private toDynamic(o: ILatestObjectRaw): ILatestObject {
        return {...o, bookmark: ko.observable(o.bookmark)};
    }

    private toStatic(o: ILatestObject): ILatestObjectRaw {
        return {...o, bookmark: o.bookmark()};
    }

    private updateStore() {
        this.silent = true;
        this._storeList(this.list().filter(o => o.bookmark()).map(this.toStatic));
        this.silent = false;
    }

}
